import {
  mdiEyeOutline,
  mdiAccountOutline,
  mdiArrowRightBold,
  mdiArrowLeftBold,
} from '@mdi/js';
import Icon from '@mdi/react';
import { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Label,
  Input,
  Table,
  Spinner,
  Col,
} from 'reactstrap';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import './EmpPerform.scss';
import { tableColumns } from './constants';
import useTableHook from '../../components/hooks/useTableHook';
import {
  FilterDropdown,
  TablePagination,
  TableSortDisplayArrow,
  UserAsyncAutocompleteSelect,
} from '../../components/Common';
import NextConfirmModal from './Components/NextConfirmModal/NextConfirmModal';
import PrevConfimation from './Components/PrevConfirmation/PrevConfimation';
import { calibrationList } from '../../store/apis/calibrationService';
import { fetchDelegateApprovalAutoComplete } from '../../store/apis/autocompleteService';
import { Messages } from '../../common/messages';
import { getCompanyCode } from '../../config/axios.config';
import { fetchCalibrationWeightageNDueDate } from '../../store/apis/performanceService';
import { getCurrentFinancialPeriod } from '../../utils/date.util';
import { showDueDate } from '../Performance/common';
import AcceptSubmitModal from '../Performance/Components/AcceptSubmitModal/AcceptSubmitModal';

function EmployeePerformance() {
  document.title = 'Employee Performance | Envision Daikin';
  const navigate = useNavigate();
  const companyCode = getCompanyCode();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [modalSubmit, setModalSubmit] = useState(false);

  const { data: performanceDueDate } = useQuery({
    queryKey: ['performanceDueDate', selectedEmployee],
    queryFn: () =>
      fetchCalibrationWeightageNDueDate({
        type: 'duedate',
        coCd: companyCode,
        fiscalYear: getCurrentFinancialPeriod(),
      }),
  });

  const dueDateData = performanceDueDate?.result?.[0];

  const initialDefaultExternalFilters = {
    empCd: null,
    type: null,
  };

  const {
    data,
    columnData,
    pageRequest,
    setPageRequest,
    externalFilters,
    applyFilter,
    columnName,
    setColumnName,
    nextPage,
    prevPage,
    handleSetExternalFilters,
    applyExternalFilters,
    setPageLoadTrigger,
    handleManualSort,
    checkColumnIfSorted,
    isLoadingData,
    isLoadingColumn,
    getPageSize,
    setPageSize,
    handleAutoSort,
    refetch,
  } = useTableHook({
    defaultPageRequest: {
      ...initialDefaultExternalFilters,
      pageNumber: 1,
      pageSize: 50,
      sortColumn: 'RequestId',
      sortDirection: 'ASC',
      filters: [],
    },
    fetchFn: calibrationList,
    onPageLoadTrigger: false,
  });

  useEffect(() => {
    setPageLoadTrigger(true);
    setPageRequest(prev => ({
      ...prev,
    }));
  }, [setPageRequest, setPageLoadTrigger]);

  const [modalMovePrev, setModalMovePrev] = useState(false);
  const toggleMovePrev = (emp = null) => {
    if (!modalMovePrev && emp) {
      setSelectedEmployee(emp);
    } else {
      setSelectedEmployee(null);
    }
    setModalMovePrev(!modalMovePrev);
  };

  const [modalMoveNext, setModalMoveNext] = useState(false);
  const toggleMoveNext = (emp = null) => {
    // Accept & Submit button only for Calibration_Result = 2 (Confirmed)
    if (emp?.Calibration_Result === 2) {
      setSelectedEmployee(emp);
      setModalSubmit(true);
      return;
    }

    if (!modalMoveNext && emp) {
      setSelectedEmployee(emp);
    } else {
      setSelectedEmployee(null);
    }
    setModalMoveNext(!modalMoveNext);
  };

  const renderTableBody = () => {
    const tableData = Array.isArray(data?.data) ? data.data : [];

    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={9}>
            {isLoadingData ? (
              <>
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              Messages.NO_EMP_PERFORMANCE
            )}
          </td>
        </tr>
      );
    }

    return tableData?.map((item, index) => (
      <tr key={index}>
        <td>{item?.EmpName}</td>
        <td>{item?.Designation}</td>
        <td>
          {`${item?.ModifiedDate ? moment(item?.ModifiedDate).format('DD/MM/YYYY') : '-'}`}
        </td>
        <td>
          {item?.CurrentStage === 'Not Started'
            ? 'Result Accepted'
            : item?.CurrentStage}
        </td>
        <td>{showDueDate(item, dueDateData)}</td>
        <td>{item?.Overall_Rating_Manager ?? 'Rating not yet provided'}</td>
        <td>{item?.Overall_Rating_Calibration ?? 'Rating not yet provided'}</td>
        <td>
          <div className="d-flex flex-row align-items-center justify-content-center gap-1">
            <button
              title="View Document"
              type="button"
              className="btn btn-link p-0"
              onClick={() => navigate(`/hr/performance/${item?.EmpCd}`)}
            >
              <Icon path={mdiEyeOutline} size={0.7} color="#017ebd" />
            </button>
            <div title="Move to Previous Step">
              <button
                title="Move to Previous Step"
                type="button"
                className="btn btn-link p-0"
                onClick={() => toggleMovePrev(item)}
                disabled={
                  item?.CurrentStage === 'Define Objectives' ||
                  item?.Calibration_Result === 2
                }
              >
                <Icon path={mdiArrowLeftBold} size={0.7} color="#017ebd" />
              </button>
            </div>
            <div title="Move to Next Step">
              <button
                title="Move to Next Step"
                type="button"
                className="btn btn-link p-0"
                onClick={() => toggleMoveNext(item)}
                disabled={
                  (item?.Manager_Review === 2 &&
                    item?.Calibration_Result === 1) ||
                  item?.CurrentStage === 'Not Started'
                }
              >
                <Icon path={mdiArrowRightBold} size={0.7} color="#017ebd" />
              </button>
            </div>
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <div className="page-content main-table-page">
      <Card className="cb-card enquiry-card">
        <CardHeader className="cb-header">
          <div>
            <span className="fs-5 fw-semibold">Employee Performance</span>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <div className="external-filter gap-2 mx-3 p-3">
            <Col className="external-filter-col">
              <Label>
                <b> Employee </b>
              </Label>
              <UserAsyncAutocompleteSelect
                autoCompleteFn={input =>
                  fetchDelegateApprovalAutoComplete({
                    prefixText: input,
                  })
                }
                mappedFn={rawData =>
                  rawData.map(item => ({
                    value: item.ID,
                    label: item.Name,
                    name: item.Name,
                    code: item.Cd,
                    designation: item.Designation,
                    image: item?.Imagefile,
                  }))
                }
                onChange={e => handleSetExternalFilters('empCd', e)}
                value={externalFilters?.empCd}
                placeholder="Search by Employee Name & Code"
                isClearable
                isSearchable
                inputIcon={
                  <Icon path={mdiAccountOutline} size={0.8} color="#667085" />
                }
                rightKeyAssessor="code"
              />
            </Col>
            <Col className="external-filter-col">
              <Label>
                <b> Status </b>
              </Label>
              <Input
                type="select"
                className="form-control external-filter-input"
                value={externalFilters.type}
                onChange={e => handleSetExternalFilters('type', e.target.value)}
              >
                <option value="">All</option>
                <option value="Define_Objectives">Define Objectives</option>
                <option value="Confirm_Objectives">Confirm Objectives</option>
                <option value="Self_Assessment">Self Assessment</option>
                <option value="Manager_Review">Manager Review</option>
                <option value="Calibration_Result">Calibration Result</option>
                <option value="Confirmation_Result">Confirm Result</option>
                <option value="Result_Accepted">Result Accepted</option>
              </Input>
            </Col>
            <Col>
              <div className="external-filter-btns gap-2">
                <button
                  type="button"
                  className="custom-btn"
                  onClick={() => {
                    applyExternalFilters({
                      ...externalFilters,
                      empCd: externalFilters.empCd?.code,
                    });
                  }}
                >
                  Search
                </button>
              </div>
            </Col>
          </div>
          <div className="table-responsive performance my-2 table-container">
            <Table
              hover
              className="custom-table align-middle text-nowrap"
              id="empl-performance"
            >
              <thead>
                <tr>
                  {Object.entries(tableColumns || {}).map(([key, value]) => (
                    <th key={key} className="px-2">
                      <div className="d-flex flex-row align-items-center justify-content-start ">
                        <div
                          className="px-1 cursor-pointer"
                          onClick={() => handleAutoSort(key)}
                        >
                          {value}
                        </div>
                        <TableSortDisplayArrow
                          direction={checkColumnIfSorted(key)}
                        />
                        <FilterDropdown
                          handleSetColumnName={() => {
                            setColumnName(key);
                          }}
                          setColumnName={setColumnName}
                          columnData={columnData}
                          applyFilter={applyFilter}
                          columnName={columnName}
                          pageRequest={pageRequest}
                          setPageRequest={setPageRequest}
                          handleManualSort={handleManualSort}
                          isLoading={isLoadingColumn}
                        />
                      </div>
                    </th>
                  ))}
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </Table>
          </div>
        </CardBody>
        <CardFooter className="py-2">
          <div>
            <TablePagination
              nextPage={nextPage}
              prevPage={prevPage}
              totalPages={data?.totalPages}
              currentPage={data?.currentPage}
              hasPreviousPage={data?.hasPreviousPage}
              hasNextPage={data?.hasNextPage}
              getPageSize={getPageSize}
              setPageSize={setPageSize}
              totalCount={data?.totalCount}
            />
          </div>
        </CardFooter>
      </Card>
      <PrevConfimation
        isOpen={modalMovePrev}
        toggle={toggleMovePrev}
        headerData={selectedEmployee}
        refreshTable={refetch}
      />
      <NextConfirmModal
        isOpen={modalMoveNext}
        toggle={toggleMoveNext}
        headerData={selectedEmployee}
        refreshTable={refetch}
      />
      <AcceptSubmitModal
        isOpen={modalSubmit}
        toggle={() => setModalSubmit(false)}
        selectedEmployee={selectedEmployee}
        refetchPerformanceHeader={refetch}
      />
    </div>
  );
}

export default EmployeePerformance;
